/**
 * The Migrada mark: a lowercase "mi" whose i is dotted with a star, the goal
 * you are moving towards. Traced from the August master
 * (public/brand/migrada-mark-lowercase-1024.png) into one vector path, set on
 * a rounded tile. Inline SVG, so it is sharp at every size and needs no
 * request. public/brand/migrada-mark*.svg hold the same drawing for <img>
 * uses, favicons and the share image. Change both together.
 *
 *   ink  → ink tile, white mark. Default, for light backgrounds.
 *   teal → teal tile, ink mark. For dark backgrounds and app icons.
 */
/** The star that dots the i: the goal you are moving towards. */
export const STAR_PATH =
  "M53.37 13.06L54.69 16.76L56.56 16.79L56.69 16.83L58.56 16.85L58.63 16.88L55.62 19.19L55.52 19.31L56.59 22.94L56.60 23.00L56.56 23.05L53.38 20.86L50.12 23.06L50.10 23.00L51.21 19.31L51.06 19.16L48.09 16.88L48.19 16.85L50.00 16.83L50.12 16.79L52.00 16.77L52.13 16.50L53.23 13.38Z";
/** The "m" and the stem of the "i". */
export const LETTERS_PATH =
  "M19.78 24.12L21.00 24.07L21.81 24.11L22.38 24.17L23.62 24.42L24.25 24.61L24.94 24.87L25.31 25.05L25.81 25.35L26.47 25.88L27.08 26.50L27.88 27.59L28.42 26.94L29.12 26.23L29.75 25.73L30.56 25.23L31.50 24.79L32.38 24.49L33.50 24.24L34.50 24.11L35.50 24.07L36.69 24.11L38.00 24.30L38.94 24.55L39.88 24.92L40.62 25.35L41.31 25.86L42.00 26.53L42.40 27.00L42.83 27.62L43.15 28.19L43.52 29.00L43.76 29.69L44.01 30.56L44.20 31.50L44.33 32.44L44.40 33.12L44.45 34.56L44.45 50.81L44.41 50.88L35.62 50.88L35.60 50.81L35.60 36.19L35.58 35.50L35.51 34.75L35.39 34.19L35.15 33.56L34.89 33.12L34.55 32.75L34.25 32.49L33.75 32.24L33.44 32.16L32.75 32.10L32.00 32.16L31.50 32.29L31.00 32.54L30.58 32.88L30.23 33.25L29.92 33.75L29.67 34.38L29.49 35.12L29.42 35.62L29.35 36.62L29.36 50.81L29.33 50.88L20.56 50.89L20.52 50.81L20.52 36.06L20.39 34.75L20.21 34.06L19.97 33.50L19.75 33.15L19.38 32.74L18.94 32.43L18.44 32.23L17.75 32.12L17.38 32.10L16.62 32.16L16.12 32.29L15.69 32.48L15.32 32.75L14.99 33.12L14.68 33.62L14.42 34.25L14.29 34.81L14.17 35.88L14.13 36.94L14.12 50.83L14.06 50.89L5.31 50.88L5.28 24.62L5.31 24.57L5.38 24.57L13.06 24.58L13.88 27.33L14.48 26.62L15.12 25.97L15.56 25.61L16.25 25.17L17.00 24.80L17.81 24.48L18.56 24.29ZM48.93 24.62L49.00 24.57L57.69 24.57L57.75 24.57L57.78 24.62L57.78 50.81L57.75 50.88L49.00 50.89L48.93 50.81Z";
const MARK_PATH = STAR_PATH + LETTERS_PATH;
/** Where the star sits inside the 64 x 64 tile, after the mark's own scaling. */
export const MARK_INNER = "translate(32 32.6) scale(0.8) translate(-32 -32)";

const TONES = {
  ink: { tile: "#0A0F0E", mark: "#FFFFFF" },
  teal: { tile: "#2EE6D6", mark: "#0A0F0E" },
} as const;

export type LogoTone = keyof typeof TONES;

export function LogoMark({ tone = "ink", size = 36, className = "" }: { tone?: LogoTone; size?: number; className?: string }) {
  const t = TONES[tone];
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" className={`shrink-0 ${className}`} aria-hidden="true" focusable="false">
      <rect width="64" height="64" rx="18" fill={t.tile} />
      <g transform={MARK_INNER}>
        <path d={MARK_PATH} fill={t.mark} />
      </g>
    </svg>
  );
}
/** Star colours for the wordmark: deep enough to hold on paper, bright on ink. */
const WORD_STAR = { ink: "#00B3A4", teal: "#2EE6D6" } as const;

/**
 * The wordmark: "migrada" in Onest 900, its i dotted with the star from the
 * mark. One shape, no tile next to it, so headers stay as quiet as the
 * words. The tile (LogoMark) is for places that need an icon: favicons, the
 * app bar on phones, team avatars, the loader.
 *
 * The i is a dotless ı with the star placed where its dot would be, so the
 * letters keep the font's own spacing. Screen readers hear "migrada".
 * `size` is the old tile size, kept so every caller stays in proportion.
 */
export function Logo({
  tone = "ink",
  size = 36,
  wordColor = "#0A0F0E",
  className = "",
}: {
  tone?: LogoTone;
  size?: number;
  wordColor?: string;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label="migrada"
      className={`inline-flex items-baseline font-black leading-none tracking-[-0.055em] ${className}`}
      style={{ fontFamily: "'Onest', system-ui, sans-serif", fontSize: Math.round(size * 0.86), color: wordColor }}
    >
      <span aria-hidden="true">m</span>
      <span aria-hidden="true" className="relative inline-block">
        {"\u0131"}
        <svg
          viewBox="48 13 11 10.2"
          className="absolute left-1/2 overflow-visible"
          style={{ top: "-0.1em", width: "0.36em", height: "0.36em", transform: "translateX(-46%)" }}
          focusable="false"
        >
          <path d={STAR_PATH} fill={WORD_STAR[tone]} />
        </svg>
      </span>
      <span aria-hidden="true">grada</span>
    </span>
  );
}
